import * as React from 'react'

import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  ColumnContent,
  Flex,
  HStack,
  IconButton,
  Stack,
  TertiaryButton,
  Text,
  TextButton,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronUpRegular,
} from '@enechain/ecloud-icons'
import { DeliveryPeriodType } from '@enechain/eposition-proto/types/v1/deal_pb'
import { Card, SpreadTypeForm } from '@enechain/eposition-ui'

import { LegInputSection } from '~/pages/Simulation/components/OrderPatternInputArea/LegInputSection/LegInputSection'
import { SummaryTransactionInputSection } from '~/pages/Simulation/components/OrderPatternInputArea/SummaryTransactionInputSection/SummaryTransactionInputSection'
import { TransactionOutlineForm } from '~/pages/Simulation/components/OrderPatternInputArea/TransactionOutlineForm'
import { OrderSummaryText } from '~/pages/Simulation/components/OrderSummaryText'
import { OrderSimulationSchema } from '~/pages/Simulation/schemas'

type Props = {
  index: number
}
export const SimulationOrderPatternForm: React.FC<Props> = ({ index }) => {
  const { t } = useTranslation(['page', 'domain', 'common', 'validation'])
  const formMethods = useFormContext<OrderSimulationSchema>()
  const [isOpen, setIsOpen] = React.useState(true)
  const watchedIsPhysicalFuture = useWatch({
    name: `orderPattern.${index}.isPhysicalFuture`,
    control: formMethods.control,
  })
  const watchedBaseDeliveryPeriodType = useWatch({
    name: `orderPattern.${index}.baseSummaryOrder.deliveryPeriodType`,
    control: formMethods.control,
  })
  const watchedExchangeDeliveryPeriodType = useWatch({
    name: `orderPattern.${index}.exchangeSummaryOrder.deliveryPeriodType`,
    control: formMethods.control,
  })

  const orderPatternErrors = formMethods.formState.errors?.orderPattern?.[index]

  const isSplitDateRange =
    watchedIsPhysicalFuture &&
    (watchedBaseDeliveryPeriodType === DeliveryPeriodType.MULTI_MONTH ||
      watchedExchangeDeliveryPeriodType === DeliveryPeriodType.MULTI_MONTH)
  const hasErrors = orderPatternErrors !== undefined
  const showError = !isOpen && hasErrors

  return (
    <Card backgroundColor={hasErrors ? 'Surface.negativeLow' : 'transparent'}>
      <Stack gap="4">
        <Flex width="full" justifyContent="space-between" alignItems="center">
          <Stack gap="0">
            <HStack gap="4">
              <Text textStyle="headerMd">
                {t('page:simulation.input_order')}
                {index + 1}
              </Text>
              <OrderSummaryText index={index} />
            </HStack>
            {showError && (
              <HStack gap="0">
                <Text color="Text.negativeMid" textStyle="textSm">
                  {t('validation:errors.invalid_union')}
                </Text>
                <TextButton
                  color="Text.negativeMid"
                  textStyle="textSm"
                  onClick={() => setIsOpen(true)}
                  paddingX="0"
                  textDecoration="underline"
                >
                  {t('common:action.positive.open')}
                </TextButton>
              </HStack>
            )}
          </Stack>
          <IconButton
            aria-label={
              isOpen
                ? t('common:action.negative.close')
                : t('common:action.positive.open')
            }
            backgroundColor="transparent"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <FaChevronUpRegular /> : <FaChevronDownRegular />}
          </IconButton>
        </Flex>

        {isOpen && (
          <Stack gap="6" paddingTop="2">
            <ColumnContent
              label={t('page:simulation.order_outline')}
              headerSize="Xs"
            >
              <TransactionOutlineForm formMethods={formMethods} index={index} />
            </ColumnContent>
            <ColumnContent
              label={t('page:simulation.spread_input')}
              headerSize="Xs"
            >
              <SpreadTypeForm
                schemaKeyName={`orderPattern.${index}.spreadType`}
                control={formMethods.control}
                hiddenDiffUnitPrice
              />
            </ColumnContent>
            <ColumnContent
              label={t('page:simulation.deal_detail')}
              headerSize="Xs"
            >
              <SummaryTransactionInputSection
                formMethods={formMethods}
                index={index}
              />
            </ColumnContent>
            {isSplitDateRange === true && (
              <ColumnContent
                label={t('domain:model.simulation.column.leg')}
                headerSize="Xs"
              >
                <LegInputSection formMethods={formMethods} index={index} />
              </ColumnContent>
            )}
            <Flex justifyContent="center">
              <TertiaryButton onClick={() => setIsOpen(false)}>
                {t('common:action.negative.close')}
              </TertiaryButton>
            </Flex>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
